import { useCallback, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { useTemplatesStudio } from 'api/useTemplatesStudio';
import { useUserDetails } from 'api/useUserDetails';
import { TemplateStudioModel } from 'common/contracts';
import { EEventsType } from 'constants/enums';
import { useBigQueryEventsTracking } from 'hooks/useBigQueryTracking';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { downloadJsonFile } from 'utils/fileUtils';
import { trackMixpanelEvent } from 'utils/mixpanelClient';
import { EToasterStatus, showToast } from 'utils/showToast';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from 'components/ui';

interface Action {
  label: string;
  handler: (data: TemplateStudioModel) => void;
  icon?: React.ElementType;
  disabled?: boolean;
  hidden?: boolean;
}

interface ActionCellProps {
  template: TemplateStudioModel;
  menuLabel?: string;
}

export const TemplateActions: React.FC<ActionCellProps> = ({
  template,
  menuLabel
}) => {
  const { publisherId } = useParams();
  const { trackBigQueryEvent } = useBigQueryEventsTracking();
  const { createTemplate, getTemplates } = useTemplatesStudio(publisherId);
  const navigate = useNavigate();
  const { setTemplateIdToActivate, setTemplateIdToDelete } =
    useTemplateStudioState();
  const { userDetails } = useUserDetails();

  const { isActive, templateId, templateName } = template;

  const handleExport = useCallback(async () => {
    try {
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${templateName}-${timestamp}`;

      const templateObjectToExport = {
        publisherId,
        template
      };

      downloadJsonFile(templateObjectToExport, filename);

      showToast({
        message: 'Template exported successfully',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      console.error('Export error:', error);
      showToast({
        message: 'Failed to export template',
        status: EToasterStatus.ERROR
      });
    } finally {
      trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
        publisher_id: publisherId,
        user_id: userDetails?.userId,
        company_id: userDetails?.company,
        templateId: templateId,
        templateAction: 'export',
        isActiveTemplate: isActive,
        templateName: templateName
      });
      trackBigQueryEvent({
        eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
        publisherId: publisherId,
        userId: userDetails?.userId,
        template_index: templateId,
        action: 'export',
        is_active: isActive,
        template_name: templateName
      });
    }
  }, [template, templateName]);

  const handleDuplicateTemplate = useCallback(async () => {
    const {
      createdAt,
      updatedAt,
      isActive,
      templateId,
      version,
      ...templateBasicData
    } = template;

    await createTemplate.mutateAsync(
      {
        ...templateBasicData,
        templateName: `${templateName} duplicated`
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Template was duplicated successfully',
            status: EToasterStatus.SUCCESS
          });
          getTemplates.refetch();
        },
        onError: () => {
          showToast({
            message: 'Error creating template',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  }, [template, templateName]);

  const actions: Action[] = useMemo(
    () => [
      {
        label: 'Edit',
        handler: () => {
          navigate(`./form/${templateId}`);
          trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
            publisher_id: publisherId,
            user_id: userDetails?.userId,
            company_id: userDetails?.company,
            templateId: templateId,
            templateAction: 'edit',
            isActiveTemplate: isActive,
            templateName: templateName
          });
          trackBigQueryEvent({
            eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
            publisherId: publisherId,
            userId: userDetails?.userId,
            template_index: templateId,
            action: 'edit',
            is_active: isActive,
            template_name: templateName
          });
        }
      },
      {
        label: 'Export',
        handler: handleExport
      },
      {
        label: 'Schedule',
        handler: () => {
          // todo: support schedule
          showToast({
            message: 'Not supported yet',
            status: EToasterStatus.ERROR
          });
        },
        hidden: true
      },
      {
        label: 'Duplicate',
        handler: handleDuplicateTemplate
      },
      {
        label: 'Activate',
        handler: () => {
          setTemplateIdToActivate(templateId);
          trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
            publisher_id: publisherId,
            user_id: userDetails?.userId,
            company_id: userDetails?.company,
            templateId: templateId,
            templateAction: 'activate',
            isActiveTemplate: false,
            template_name: templateName
          });
          trackBigQueryEvent({
            eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
            publisherId: publisherId,
            userId: userDetails?.userId,
            template_index: templateId,
            action: 'activate',
            is_active: false,
            template_name: templateName
          });
        },
        hidden: isActive
      },
      {
        label: 'Delete',
        handler: () => {
          setTemplateIdToDelete(templateId);

          trackMixpanelEvent(EEventsType.DASHBOARD_TEMPLATE_ACTION, {
            publisher_id: publisherId,
            user_id: userDetails?.userId,
            company_id: userDetails?.company,
            templateId: templateId,
            templateAction: 'delete',
            isActiveTemplate: false,
            templateName: templateName
          });
          trackBigQueryEvent({
            eventName: EEventsType.DASHBOARD_TEMPLATE_ACTION,
            publisherId: publisherId,
            userId: userDetails?.userId,
            template_index: templateId,
            action: 'delete',
            is_active: false,
            template_name: templateName
          });
        },
        hidden: isActive
      }
    ],
    [isActive, templateId]
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex items-center justify-center w-[36px] h-[36px] cursor-pointer rounded-full border border-base-input bg-base-background select-none">
          <FontAwesomeIcon icon="fa-regular fa-ellipsis-vertical" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="left"
        align="start"
        sideOffset={0}
        className="bg-base-popover border-base-border rounded-md w-auto min-w-0"
      >
        {menuLabel && (
          <DropdownMenuLabel className="border-b border-base-muted-foreground">
            {menuLabel}
          </DropdownMenuLabel>
        )}
        {actions
          .filter((action: Action) => !action.hidden)
          .map((action: Action, index: number) => (
            <DropdownMenuItem
              key={index}
              onClick={() => action.handler(template)}
              className="text-base-popoverForeground text-p-s hover:bg-base-accent cursor-pointer hover:rounded-md"
              disabled={action.disabled}
            >
              {action.icon && <action.icon className="mr-2" />}
              {action.label}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
